
import map from "lodash-es/map";
import filter from "lodash-es/filter";

import "es6-promise";

import * as pluralize from "pluralize";
import axios, { AxiosResponse } from "axios";
import Vue, { ComponentOptions } from "vue";
import Component from "vue-class-component";
import {
  Emit,
  Inject,
  Model,
  Prop,
  Provide,
  Watch
} from "vue-property-decorator";

import VueMarkdown from "vue-markdown";

import RemoteFileSelectAboutBox from "./RemoteFileSelectAboutBox";
import FileList from "../FileList";
import NestedFileList from "../NestedFileList";
import { LaxyFile, Sample } from "../../model";
import { ADD_SAMPLES } from "../../store";
import { WebAPI } from "../../web-api";

import { ENADummySampleList as _dummysampleList } from "../../test-data";
import {
  EMPTY_TREE_ROOT,
  FileListItem,
  fileListToTree,
  findPair,
  flattenTree,
  is_archive_url,
  objListToTree,
  simplifyFastqName,
  TreeNode
} from "../../file-tree-util";
import { Snackbar } from "../../snackbar";

import { longestCommonPrefix, longestCommonSuffix } from "../../prefix";
import { escapeRegExp, reverseString } from "../../util";

interface DbAccession {
  accession: string;
}

@Component({
  components: {
    RemoteFileSelectAboutBox,
    VueMarkdown,
    FileList,
    NestedFileList
  },
  props: {},
  filters: {}
})
export default class RemoteFileSelect extends Vue {
  @Prop({
    default: "https://example.com/my-files/page-of-links/",
    type: String
  })
  public placeholder: string;

  @Prop({ default: true, type: Boolean })
  public showButtons: boolean;

  @Prop({ default: "Add to cart", type: String })
  public addButtonLabel: string;

  @Prop({ default: false, type: Boolean })
  public singleSelect: boolean;

  @Prop({ default: true, type: Boolean })
  public autoSelectPair: boolean;

  @Prop({ default: true, type: Boolean })
  public showAboutBox: boolean;

  @Prop({ default: "", type: String })
  public startUrl: string;

  public url: string = "";
  public initialUrl: string = ""; // the URL initially submitted to the form, for tracking navigation state
  public navigatedUrl: string = "";
  public previousUrl: string = "";
  public listing: Array<any> = [];

  public selectedFiles: FileListItem[] = [];

  public snackbar_message: string = "Everything is fine. ☃";
  public snackbar_duration: number = 2000;

  public submitting: boolean = false;
  public error_alert_message: string = "Everything is fine.";

  created() {}

  @Watch("startUrl")
  onStartUrlChanged(newVal?: string, oldVal?: string) {
    if (this.startUrl.trim() !== "") {
      this.url = this.startUrl.trim();
      this.submitUrl(this.url);
    }
  }

  mounted() {
    this.onStartUrlChanged();
  }

  get fileTree(): TreeNode<FileListItem> {
    // This file tree isn't actually a tree, but a single (non-nested) list of children on the root node.
    // This allows us to re-use the NestedFileList component.
    // Directory nodes are given 'meta.onclick' callbacks that trigger retrieving their remote listing, and
    // resulting in an update of this item listing.
    if (this.listing) {
      // Turn the list of items into a (single level) TreeNode object
      const tree = objListToTree<FileListItem>(
        this.listing,
        (i: FileListItem) => {
          return [i.name];
        },
        (i: FileListItem) => {
          return i.name;
        }
      );
      // const tree = fileListToTree<FileListItem>(this.listing);
      for (let node of tree.children) {
        const item = node.obj as FileListItem;
        node.meta.type = item.type;
        node.meta.tags = item.tags;
        // add onclick callbacks to directory and archive nodes
        if (
          item &&
          (item.type === "directory" || item.tags.includes("archive"))
        ) {
          node.meta.onclick = () => this.listLinks(item.location);
        }
      }
      return tree;
    } else {
      return EMPTY_TREE_ROOT;
    }
  }

  get hasResults() {
    return !(this.listing == null || this.listing.length === 0);
  }

  urlIsParentPath(longurl: string, shorturl: string): boolean {
    return longurl.length > shorturl.length && longurl.indexOf(shorturl) === 0;
  }

  onSelect(rows: any) {
    // console.log(rows);
    // const fileList = this.$refs['remote-files-list'] as NestedFileList;
    // if (fileList) {
    //     this.selectedFiles = fileList.selectedFiles;
    // }
    this.selectedFiles = filter(rows, { type: "file" }) as FileListItem[];
  }

  remove(rows: LaxyFile[]) {
    for (const row of rows) {
      const i = this.listing.indexOf(row);
      this.listing.splice(i, 1);
    }
  }

  addToCart() {
    this.$emit("files-added", this.selectedFiles);
  }

  // rowHover(row: any) {
  //     this.hoveredSampleDetails = row;
  //     // console.log(row);
  // }

  async submitUrl(url: string) {
    url = url.trim();
    this.url = url;
    this.initialUrl = url;
    await this.listLinks(url);
  }

  async listLinks(url: string) {
    this.listing = [];
    try {
      this.submitting = true;
      const response = await WebAPI.remoteFilesList(url);
      this.submitting = false;
      this.previousUrl = `${this.navigatedUrl}`;
      this.navigatedUrl = url;
      this.url = url;
      this.populateSelectionList(response.data);
    } catch (error) {
      console.log(error);
      this.error_alert_message = error.toString();
      this.openDialog("error_dialog");
      this.submitting = false;
    }
  }

  populateSelectionList(data: any) {
    // console.dir(data);
    this.listing = [];
    if (is_archive_url(this.navigatedUrl)) {
      // add a '..' entry to navigate out of TAR archive
      const parts = this.navigatedUrl.split("/");
      const archive_name = parts.pop();
      const parentDir = parts.join("/");
      this.listing.push({
        location: parentDir,
        name: "..",
        type: "directory",
        tags: []
      } as FileListItem);
    }
    for (let f of data["listing"]) {
      if (f.type === "directory") {
        // don't allow navigation down the tree below the initially submitted URL
        if (this.urlIsParentPath(this.initialUrl, f.location)) {
          continue;
        }

        // rename URLs that lead down toward the base of the tree
        if (this.urlIsParentPath(this.navigatedUrl, f.location)) {
          f.name = "..";
          // or skip them ?
          // continue;
        }

        if (f.meta === undefined) f.meta = {};
        f.meta.onclick = () => this.listLinks(f["location"]);
      }
      this.listing.push(f);
    }
    // console.log(data['archives']);
  }

  showErrorDialog(message: string) {
    this.error_alert_message = message;
    this.openDialog("error_dialog");
  }

  openDialog(ref: string) {
    (this.$refs[ref] as MdDialog).open();
  }
}
